import { useState, useEffect } from "react";
import { useLanguage } from "./LanguageContext";
import type { InvestorPage } from "@/store/slices/navigationSlice";

interface DistributionData {
  key: "dist.long" | "dist.rooms" | "dist.short";
  percentage: number;
  color: string;
}

interface SimpleDonutChartProps {
  className?: string;
  onPageChange?: (page: InvestorPage) => void;
  data?: DistributionData[];
}

export function SimpleDonutChart({
  className = "",
  onPageChange,
  data: dataProp,
}: SimpleDonutChartProps) {
  const { t } = useLanguage();
  const [isAnimated, setIsAnimated] = useState(false);
  const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipData, setTooltipData] = useState<DistributionData | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Distribution data
  const data: DistributionData[] = dataProp ?? [
    { key: "dist.long", percentage: 9.3, color: "#111827" },
    { key: "dist.rooms", percentage: 32.5, color: "#6B7280" },
    { key: "dist.short", percentage: 58.15, color: "#D1D5DB" },
  ];

  // Override labels to estandarizar leyenda del donut
  const labelFor = (key: DistributionData["key"]) => {
    switch (key) {
      case "dist.long":
        return "Tradicional";
      case "dist.rooms":
        return "Habitaciones";
      case "dist.short":
      default:
        return "Temporal";
    }
  };

  const handleClick = () => {
    if (onPageChange) {
      onPageChange("portfolio");
    }
  };

  // Calculate cumulative percentages for the donut
  let cumulativePercentage = 0;
  const slices = data.map((item) => {
    const slice = {
      ...item,
      startPercentage: cumulativePercentage,
      endPercentage: cumulativePercentage + item.percentage,
    };
    cumulativePercentage += item.percentage;
    return slice;
  });

  // SVG donut configuration
  const size = 220;
  const strokeWidth = 22;
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  // Animation trigger - rápido y sincronizado
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimated(true);
    }, 100); // Inicio más inmediato
    return () => clearTimeout(timer);
  }, []);

  const handleSliceHover = (
    slice: DistributionData,
    event: React.MouseEvent
  ) => {
    setHoveredSlice(slice.key);
    setTooltipData(slice);
    setTooltipVisible(true);
    updateTooltipPosition(event);
  };

  const handleSliceMove = (event: React.MouseEvent) => {
    if (tooltipVisible) {
      updateTooltipPosition(event);
    }
  };

  const updateTooltipPosition = (event: React.MouseEvent) => {
    const tooltipHeight = 80; // Altura estimada del tooltip
    const margin = 16; // Margen desde el cursor

    // Detectar si el tooltip se cortaría en la parte superior
    const wouldClipTop = event.clientY - tooltipHeight - margin < 20;

    setTooltipPosition({
      x: event.clientX,
      y: wouldClipTop
        ? event.clientY + margin // Mostrar debajo del cursor si se cortaría arriba
        : event.clientY - margin, // Mostrar arriba del cursor normalmente
    });
  };

  const handleSliceLeave = () => {
    setHoveredSlice(null);
    setTooltipVisible(false);
    setTooltipData(null);
  };

  const handleLegendHover = (slice: DistributionData) => {
    setHoveredSlice(slice.key);
  };

  const handleLegendLeave = () => {
    setHoveredSlice(null);
  };

  return (
    <>
      <div
        className={`bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer overflow-visible ${className}`}
        onClick={handleClick}
        style={{ overflow: "visible" }}
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("dist.title")}
          </h3>
        </div>

        {/* Content */}
        <div className="p-6 overflow-visible">
          {/* Chart Container with Tooltip */}
          <div className="chart-container relative flex flex-col items-center overflow-visible">
            {/* Donut Chart - Centered with overflow protection */}
            <div
              className="flex-shrink-0 mb-6 relative overflow-visible"
              style={{ padding: "16px" }}
            >
              <div
                className="relative flex items-center justify-center overflow-visible bg-transparent"
                style={{ width: "280px", height: "280px" }}
              >
                <svg
                  width="220"
                  height="220"
                  viewBox="0 0 220 220"
                  className="absolute inset-0 m-auto transform -rotate-90 z-10"
                  style={{ overflow: "visible" }}
                >
                  {/* Background circle */}
                  <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="#F3F4F6"
                    strokeWidth={strokeWidth}
                  />

                  {/* Data slices */}
                  {slices.map((slice, index) => {
                    const startAngle =
                      (slice.startPercentage / 100) * circumference;
                    const sliceLength =
                      (slice.percentage / 100) * circumference;
                    const isHovered = hoveredSlice === slice.key;

                    return (
                      <circle
                        key={slice.key}
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="none"
                        stroke={slice.color}
                        strokeWidth={isHovered ? strokeWidth + 4 : strokeWidth}
                        strokeDasharray={`${sliceLength} ${circumference}`}
                        strokeDashoffset={-startAngle}
                        className="transition-all duration-200 ease-out cursor-pointer"
                        style={{
                          opacity: isAnimated ? (isHovered ? 1 : 0.9) : 0,
                          filter: isHovered
                            ? "brightness(1.08) drop-shadow(0 6px 20px rgba(0, 0, 0, 0.12))"
                            : "none",
                          transform: isHovered ? "scale(1.03)" : "scale(1)",
                          transformOrigin: "center",
                          transition: `all 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 300ms ease-out ${
                            100 + index * 250
                          }ms, stroke-dasharray 400ms ease-out ${
                            100 + index * 250
                          }ms`,
                          strokeDasharray: isAnimated
                            ? `${sliceLength} ${circumference}`
                            : `0 ${circumference}`,
                        }}
                        onMouseEnter={(e) => handleSliceHover(slice, e)}
                        onMouseMove={handleSliceMove}
                        onMouseLeave={handleSliceLeave}
                      />
                    );
                  })}
                </svg>

                {/* Center text */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                  <div
                    className="text-center"
                    style={{
                      opacity: isAnimated ? 1 : 0,
                      transform: isAnimated ? "scale(1)" : "scale(0.8)",
                      transition:
                        "all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 850ms",
                    }}
                  >
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      100%
                    </div>
                    <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                      Total
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend - Bottom */}
            <div className="w-full max-w-lg">
              <div className="grid grid-cols-3 gap-6">
                {data.map((item, index) => (
                  <div
                    key={item.key}
                    className="flex flex-col items-center text-center group cursor-pointer transition-all duration-300 hover:bg-gray-50 rounded-lg p-4 -m-4"
                    style={{
                      opacity: isAnimated ? 1 : 0,
                      transform: isAnimated
                        ? "translateY(0)"
                        : "translateY(20px)",
                      transition: `all 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${
                        950 + index * 80
                      }ms`,
                    }}
                    onMouseEnter={() => handleLegendHover(item)}
                    onMouseLeave={handleLegendLeave}
                  >
                    <div
                      className="w-5 h-5 rounded-full flex-shrink-0 mb-3 transition-all duration-300"
                      style={{
                        backgroundColor: item.color,
                        transform:
                          hoveredSlice === item.key ? "scale(1.2)" : "scale(1)",
                        boxShadow:
                          hoveredSlice === item.key
                            ? `0 0 0 3px ${item.color}20`
                            : "none",
                      }}
                    />
                    <span className="font-medium text-gray-700 text-sm mb-2 group-hover:text-gray-900 transition-colors duration-200 leading-tight">
                      {labelFor(item.key)}
                    </span>
                    <span
                      className="font-bold text-gray-900 text-lg"
                      style={{ fontFeatureSettings: '"tnum"' }}
                    >
                      {item.percentage.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Tooltip que sigue el cursor exactamente - Fuera del contenedor */}
      {tooltipVisible && tooltipData && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform:
              tooltipPosition.y > 100
                ? "translate(-50%, -100%)"
                : "translate(-50%, 0%)",
          }}
        >
          <div
            className="relative px-6 py-4 rounded-xl shadow-2xl backdrop-blur-md border border-white/20"
            style={{
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.97) 0%, rgba(30, 41, 59, 0.97) 100%)",
              animation:
                "tooltipFadeIn 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              boxShadow:
                "0 25px 50px rgba(0, 0, 0, 0.35), 0 10px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.12)",
              minWidth: "140px",
            }}
          >
            <div className="text-xs font-medium text-gray-300 mb-2.5 opacity-95 leading-tight tracking-wide">
              {labelFor(tooltipData.key)}
            </div>
            <div
              className="font-bold text-white text-xl tracking-tight"
              style={{
                fontFeatureSettings: '"tnum"',
                letterSpacing: "-0.03em",
              }}
            >
              {tooltipData.percentage.toFixed(1)}%
            </div>

            {/* Tooltip Arrow Premium - Dinámico */}
            <div
              className={`absolute left-1/2 transform -translate-x-1/2 ${
                tooltipPosition.y > 100 ? "top-full" : "bottom-full"
              }`}
              style={{
                width: 0,
                height: 0,
                borderLeft: "9px solid transparent",
                borderRight: "9px solid transparent",
                ...(tooltipPosition.y > 100
                  ? { borderTop: "9px solid rgba(15, 23, 42, 0.97)" }
                  : { borderBottom: "9px solid rgba(15, 23, 42, 0.97)" }),
                filter: "drop-shadow(0 3px 6px rgba(0, 0, 0, 0.25))",
              }}
            />

            {/* Subtle glow effect */}
            <div
              className="absolute inset-0 rounded-xl"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 60%)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
